import { useState, useEffect, useCallback, useRef } from "react";
import axiosClient from "../api/axiosClient";
import { toast } from "react-toastify";

// Configuración del caché
const CACHE_KEY = "stripe_dashboard_data";
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutos
const STALE_TIME = 2 * 60 * 1000; // 2 minutos - después de este tiempo, los datos se consideran "stale" pero aún válidos

/**
 * Hook personalizado para manejar el caché de datos de Stripe
 * Implementa estrategias de caché inteligente con persistencia en localStorage
 */
export const useStripeDataCache = (user, stripeStatus, enabled = true) => {
	const [data, setData] = useState({
		last30DaysRevenue: 0,
		totalRevenue: 0,
		ticketsSold: 0,
		totalTicketsSold: 0,
		platformFee: 0,
		availableBalance: 0,
		pendingBalance: 0,
		currency: "EUR",
	});

	const [loading, setLoading] = useState(false);
	const [lastFetch, setLastFetch] = useState(null);
	const [isStale, setIsStale] = useState(false);
	const abortControllerRef = useRef(null);

	// Generar clave de caché única por usuario
	const cacheKey = `${CACHE_KEY}_${user?.id}`;

	/**
	 * Cargar datos desde localStorage
	 */
	const loadFromCache = useCallback(() => {
		try {
			const cached = localStorage.getItem(cacheKey);
			if (cached) {
				const { data: cachedData, timestamp } = JSON.parse(cached);
				const now = Date.now();
				const age = now - timestamp;

				if (age < CACHE_DURATION) {
					setData(cachedData);
					setLastFetch(timestamp);
					setIsStale(age > STALE_TIME);
					return true;
				} else {
					// Datos expirados, limpiar caché
					localStorage.removeItem(cacheKey);
				}
			}
		} catch (error) {
			console.warn("Error loading from cache:", error);
			localStorage.removeItem(cacheKey);
		}
		return false;
	}, [cacheKey]);

	/**
	 * Guardar datos en localStorage
	 */
	const saveToCache = useCallback(
		(newData) => {
			try {
				const cacheData = {
					data: newData,
					timestamp: Date.now(),
				};
				localStorage.setItem(cacheKey, JSON.stringify(cacheData));
			} catch (error) {
				console.warn("Error saving to cache:", error);
			}
		},
		[cacheKey],
	);

	/**
	 * Limpiar caché
	 */
	const clearCache = useCallback(() => {
		localStorage.removeItem(cacheKey);
		setLastFetch(null);
		setIsStale(false);
	}, [cacheKey]);

	/**
	 * Fetch datos desde la API
	 */
	const fetchData = useCallback(
		async (force = false) => {
			// Verificar si necesitamos hacer fetch
			if (!enabled || !user) {
				return;
			}

			// Si no es forzado y tenemos datos válidos en caché, no hacer fetch
			if (!force && lastFetch) {
				const age = Date.now() - lastFetch;
				if (age < CACHE_DURATION) {
					console.log(
						"📦 Using cached data, age:",
						Math.round(age / 1000),
						"seconds",
					);
					return;
				}
			}

			// Cancelar request anterior si existe
			if (abortControllerRef.current) {
				abortControllerRef.current.abort();
			}

			abortControllerRef.current = new AbortController();

			try {
				setLoading(true);
				console.log("🔄 Fetching fresh Stripe data...");

				const response = await axiosClient.get("/api/stripe/dashboard-data", {
					signal: abortControllerRef.current.signal,
				});

				if (response.data.success) {
					const newData = {
						last30DaysRevenue: response.data.data.last30DaysRevenue,
						totalRevenue: response.data.data.totalRevenue,
						ticketsSold: response.data.data.ticketsSold,
						totalTicketsSold: response.data.data.totalTicketsSold,
						platformFee: response.data.data.platformFee,
						availableBalance: response.data.data.availableBalance,
						pendingBalance: response.data.data.pendingBalance,
						currency: response.data.data.currency,
						accountInfo: response.data.data.accountInfo,
					};

					setData(newData);
					saveToCache(newData);
					setLastFetch(Date.now());
					setIsStale(false);

					if (force) {
						toast.success("Datos actualizados correctamente");
					}

					console.log("✅ Stripe data updated and cached");
				} else {
					throw new Error(
						response.data.message || "Error al obtener datos del dashboard",
					);
				}
			} catch (error) {
				if (
					error.name === "AbortError" ||
					error.name === "CanceledError" ||
					error.code === "ERR_CANCELED"
				) {
					console.log("🚫 Request aborted");
					return;
				}

				console.error("❌ Error fetching Stripe data:", error);

				// Solo mostrar error si fue un refresh manual
				if (force) {
					toast.error("Error al cargar los datos de Stripe");
				}

				// Si no tenemos datos en caché, usar valores por defecto
				if (!lastFetch) {
					setData({
						last30DaysRevenue: 0,
						totalRevenue: 0,
						ticketsSold: 0,
						totalTicketsSold: 0,
						platformFee: 0,
						availableBalance: 0,
						pendingBalance: 0,
						currency: "EUR",
						accountInfo: null,
					});
				}
			} finally {
				setLoading(false);
				abortControllerRef.current = null;
			}
		},
		[enabled, user, lastFetch, saveToCache],
	);

	/**
	 * Refresh manual (forzado)
	 */
	const refresh = useCallback(() => {
		fetchData(true);
	}, [fetchData]);

	/**
	 * Invalidar caché y recargar
	 */
	const invalidateAndRefresh = useCallback(() => {
		clearCache();
		fetchData(true);
	}, [clearCache, fetchData]);

	// Cargar datos iniciales desde caché
	useEffect(() => {
		if (!enabled || !user) {
			return;
		}

		console.log(
			"🔄 useStripeDataCache: Loading initial data for user:",
			user.id,
		);
		const hasCache = loadFromCache();

		// Si no hay caché válido, hacer fetch inicial
		if (!hasCache && !stripeStatus.loading) {
			console.log("🔄 useStripeDataCache: No cache found, fetching data");
			fetchData(false);
		} else if (hasCache) {
			console.log("📦 useStripeDataCache: Using cached data");
		}
	}, [enabled, user?.id]); // Solo depender del ID del usuario para evitar re-renders innecesarios

	// useEffect separado para manejar cambios en stripeStatus
	useEffect(() => {
		// Solo hacer fetch si el estado cambió a activo y no tenemos datos
		if (enabled && user && !stripeStatus.loading && !lastFetch) {
			console.log(
				"🔄 useStripeDataCache: Stripe status became active, fetching data",
			);
			fetchData(false);
		}
	}, [enabled, stripeStatus.loading]);

	useEffect(() => {
		if (!enabled && abortControllerRef.current) {
			abortControllerRef.current.abort();
			abortControllerRef.current = null;
			setLoading(false);
		}
	}, [enabled]);

	// Limpiar al desmontar
	useEffect(() => {
		return () => {
			if (abortControllerRef.current) {
				abortControllerRef.current.abort();
			}
		};
	}, []);

	// Calcular estado del caché
	const cacheInfo = {
		hasCache: !!lastFetch,
		age: lastFetch ? Date.now() - lastFetch : 0,
		isStale,
		isExpired: lastFetch ? Date.now() - lastFetch > CACHE_DURATION : true,
		isValid: lastFetch ? Date.now() - lastFetch < CACHE_DURATION : false,
		cacheKey,
	};

	return {
		data,
		loading,
		refresh,
		invalidateAndRefresh,
		clearCache,
		cacheInfo,
		lastFetch,
	};
};

export default useStripeDataCache;
